import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import {
  CalendarMonthRounded,
  CancelRounded,
  CheckCircleRounded,
  CreditCardRounded,
  FlashOnRounded,
  LockClockRounded,
  PlayArrowRounded,
  WarningRounded,
} from "@mui/icons-material";
import { useSubscription } from "./SubscriptionContext";
import { formatteDateDay, pageScrollStyle } from "../../styles/styles";
import PricingPage from "../../pages/LandingPages/PricingPage";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  getMySubscription,
  getMyUsage,
} from "../../redux/Apps/users/userSlice";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

const ENDED_STATUSES = new Set([
  "expired",
  "inactive",
  "cancelled",
  "canceled",
  "failed",
  "past_due",
  "payment_failed",
]);

const ACTIVE_STATUSES = new Set([
  "active",
  "trial",
  "trialing",
  "in_trial",
  "authorized",
  "authorised",
  "grace",
  "grace_period",
  "paid",
  "payment_success",
  "completed",
  "success",
]);

const limitDefinitions = [
  { label: "Group Task Limit", key: "task_list_limits" },
  { label: "Matter Limit", key: "case_limit" },
  { label: "Matter Template Limit", key: "case_template_limit" },
  { label: "Client Limit (Person | Company)", key: "client_limit" },
  { label: "Tags", key: "tags_limit" },
  // { label: "Custom Fields per Contact", key: "custom_field_limit_contact" },

  { label: "Account Type (Client Fund Account)", key: "client_account_limit" },
  {
    label: "Account Type (Law Firm Account)",
    key: "firm_account_limit",
  },
  { label: "Document Categories", key: "custom_category_document_limit" },
  { label: "Document Storage (MB)", key: "storage_limit_mb" },
  {
    label: "Uploaded Document Templates",
    key: "create_document_template_upload_limit",
  },
  {
    label: "Rich Text Document Templates",
    key: "create_document_template_quill_limit",
  },
  // { label: "Bill Downloads", key: "bill_can_download_limit" },
  { label: "Invite User Limit", key: "user_limit" },
  { label: "Custom Roles", key: "custom_role_limit" },
  { label: "Total Custom Fields", key: "custom_fields_count" },
];

const DEFAULT_USAGE_ERROR_MESSAGE =
  "We couldn't load your usage details. Please try again later.";
const SUPPORT_EMAIL = "support@lexmom.com";

const PRICING_ACTION_LABELS = new Set(
  [
    "browse plans",
    "view all plans",
    "choose a subscription",
    "upgrade plan",
    "buy a plan",
    "change plan",
  ].map((label) => label.toLowerCase())
);

const REFRESH_ACTION_LABELS = new Set(
  ["renew subscription", "refresh subscription", "retry subscription"].map(
    (label) => label.toLowerCase()
  )
);

const SUPPORT_ACTION_LABELS = new Set(
  ["contact support", "get help", "support"].map((label) => label.toLowerCase())
);

const resolveErrorMessage = (error, fallbackMessage) => {
  const seen = new WeakSet();

  const extract = (value) => {
    if (value === null || value === undefined) return null;

    if (typeof value === "string") {
      return value.trim() !== "" ? value : null;
    }

    if (Array.isArray(value)) {
      for (const entry of value) {
        const extracted = extract(entry);
        if (extracted) return extracted;
      }
      return null;
    }

    if (typeof value === "object") {
      if (seen.has(value)) return null;
      seen.add(value);

      const directCandidates = [
        value.message,
        value.detail,
        value.error,
        value.errorMessage,
        value.description,
        value.title,
        value.msg,
        value.reason,
      ];

      for (const candidate of directCandidates) {
        const extracted = extract(candidate);
        if (extracted) return extracted;
      }

      const nestedCandidates = [
        value.non_field_errors,
        value.errors,
        value.data,
        value.response?.data,
        value.response?.message,
        value.response?.statusText,
      ];

      for (const candidate of nestedCandidates) {
        const extracted = extract(candidate);
        if (extracted) return extracted;
      }
    }

    return null;
  };

  const resolved = extract(error);
  return resolved || fallbackMessage;
};

const SubscriptionActionButtons = ({
  actions,
  onOpenPricing,
  onRefreshSubscription,
  onContactSupport,
  onFallback,
}) => {
  if (!actions || actions.length === 0) {
    return null;
  }

  const handleClick = (action) => {
    const normalizedLabel = action.label?.trim().toLowerCase();

    if (!normalizedLabel) {
      onFallback?.(action);
      return;
    }

    if (PRICING_ACTION_LABELS.has(normalizedLabel)) {
      if (onOpenPricing) {
        onOpenPricing(action);
        return;
      }
    } else if (REFRESH_ACTION_LABELS.has(normalizedLabel)) {
      if (onRefreshSubscription) {
        onRefreshSubscription(action);
        return;
      }
    } else if (SUPPORT_ACTION_LABELS.has(normalizedLabel)) {
      if (onContactSupport) {
        onContactSupport(action);
        return;
      }
    }

    onFallback?.(action);
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: "1rem",
        alignItems: "center",
        flexWrap: "wrap",
        justifyContent: "flex-end",
      }}
    >
      {actions.map((action) => (
        <Button
          key={action.label}
          variant={action.variant}
          size="small"
          disabled={action.disabled}
          onClick={() => handleClick(action)}
          sx={{ mb: "0.25rem", textTransform: "capitalize" }}
        >
          {action.label}
        </Button>
      ))}
    </Box>
  );
};

const toTitleCase = (value) =>
  typeof value === "string"
    ? value
        .split(" ")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ")
    : value;

const SubscriptionStatusPage = ({ lockScreen }) => {
  const {
    subscription,
    loading,
    ready,
    hasActivePlan,
    plan,
    isTrial,
    isSubscriptionEnding,
    isSubscriptionExpired,
    subscriptionEndedReason,
    expiryDate,
    subscriptionStatus,
    getPlanName,
    subscriptionMessage,
    subscriptionErrorCode,
    subscriptionError,
  } = useSubscription();

  const [showPricingPage, setShowPricingPage] = useState(false);
  const [isRefreshingSubscription, setIsRefreshingSubscription] =
    useState(false);
  const dispatch = useDispatch();
  const theme = useTheme();
  const usageColor = theme.palette.primary.main;
  const totalColor =
    theme.palette.info?.main ||
    theme.palette.secondary?.main ||
    theme.palette.primary.light;
  const hasActiveSubscription = hasActivePlan();
  const normalizedPlan = plan && typeof plan === "object" ? plan : null;
  const { token, myUsage, usageLoading, usageError, teamMemberLawyerId } =
    useSelector((state) => state.user);

  const expiry = expiryDate ? new Date(expiryDate) : null;
  const expiredByDate = expiry ? expiry.getTime() < Date.now() : false;
  const expiryDisplay = expiry ? formatteDateDay(expiry) : null;
  const nextBillingDisplay = subscription?.next_billing_date
    ? formatteDateDay(subscription.next_billing_date)
    : null;
  const planName = getPlanName();
  const daysRemaining = expiry
    ? Math.max(
        0,
        Math.ceil((expiry.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
      )
    : null;

  const usageErrorMessage = useMemo(() => {
    if (!usageError) return null;
    return resolveErrorMessage(usageError, DEFAULT_USAGE_ERROR_MESSAGE);
  }, [usageError]);

  const numberFormatter = useMemo(() => new Intl.NumberFormat(), []);

  const formatCount = useCallback(
    (value) => {
      if (value === null || value === undefined) return "0";
      if (typeof value === "string" && value.trim() === "") return value;
      const num = Number(value);
      if (Number.isFinite(num)) {
        return numberFormatter.format(num);
      }
      return String(value);
    },
    [numberFormatter]
  );

  const usageMetrics = useMemo(() => {
    if (!normalizedPlan) return [];

    const parseNumber = (value) => {
      if (value === null || value === undefined) return null;
      if (typeof value === "number") {
        return Number.isFinite(value) ? value : null;
      }
      const num = Number(value);
      return Number.isFinite(num) ? num : null;
    };

    return limitDefinitions
      .map(({ key, label }) => {
        const totalRaw = normalizedPlan ? normalizedPlan[key] : null;
        const usageKey = `current_${key}`;
        const currentRaw =
          myUsage && typeof myUsage === "object" ? myUsage[usageKey] : null;

        if (totalRaw === undefined && currentRaw === undefined) {
          return null;
        }

        const totalNumber = parseNumber(totalRaw);
        const currentNumber = parseNumber(currentRaw);

        if (totalNumber === null && currentNumber === null) {
          return null;
        }

        const isUnlimited =
          totalNumber === null ||
          (Number.isFinite(totalNumber) && totalNumber <= 0);

        const safeCurrent = currentNumber ?? 0;
        const percentage = isUnlimited
          ? null
          : Math.max(
              0,
              Math.min(100, Math.round((safeCurrent / totalNumber) * 100))
            );

        return {
          key,
          label,
          total: totalNumber,
          current: safeCurrent,
          isUnlimited,
          percentage,
          hasUsageData: currentNumber !== null,
        };
      })
      .filter(Boolean);
  }, [normalizedPlan, myUsage]);

  const unlimitedUsageMetrics = useMemo(
    () => usageMetrics.filter((metric) => metric.isUnlimited),
    [usageMetrics]
  );

  const usageChartData = useMemo(() => {
    return usageMetrics
      .filter(
        (metric) =>
          !metric.isUnlimited &&
          metric.total !== null &&
          Number.isFinite(metric.total) &&
          metric.total > 0
      )
      .map((metric) => ({
        name: metric.label,
        total: metric.total,
        used: metric.hasUsageData ? metric.current : 0,
        hasUsageData: metric.hasUsageData,
      }));
  }, [usageMetrics]);

  const chartHasData = usageChartData.length > 0;
  const chartHasUsageValues = usageChartData.some((item) => item.hasUsageData);

  const axisTickFormatter = useCallback(
    (value) => formatCount(value),
    [formatCount]
  );

  const axisNameFormatter = useCallback((value) => {
    if (typeof value !== "string") return value;
    return value.length > 22 ? `${value.slice(0, 22)}…` : value;
  }, []);

  useEffect(() => {
    if (!token || lockScreen) return;
    dispatch(getMyUsage({ token, teamMemberLawyerId }));
  }, [dispatch, token, lockScreen, teamMemberLawyerId]);

  const statusInfo = useMemo(() => {
    const normalizedStatus = subscriptionStatus
      ? subscriptionStatus.toLowerCase()
      : null;
    const statusIsEnded = normalizedStatus
      ? ENDED_STATUSES.has(normalizedStatus)
      : false;

    if (
      subscriptionErrorCode === "subscription_inactive" ||
      subscriptionErrorCode === "inactive"
    ) {
      return {
        type: "subscription-inactive",
        palette: "error",
        icon: CancelRounded,
        title: "Subscription inactive",
        description:
          subscriptionMessage ||
          "Your subscription is currently inactive. Please renew to continue using Lexmom.",
        primaryActionLabel: "Buy a plan",
      };
    }

    if (loading || !ready) {
      return {
        type: "loading",
        palette: "info",
        icon: WarningRounded,
        title: "Checking subscription...",
        description:
          "We're verifying your subscription status. Please wait a moment.",
        primaryActionLabel: null,
      };
    }

    if (!normalizedPlan && !isTrial) {
      return {
        type: "no-plan",
        palette: "info",
        icon: CreditCardRounded,
        title: "No subscription yet",
        description:
          "You haven't selected a plan. Choose a subscription to unlock all features.",
        primaryActionLabel: "Browse plans",
      };
    }

    if (isTrial) {
      if (
        expiredByDate ||
        isSubscriptionExpired ||
        subscriptionEndedReason === "expired"
      ) {
        return {
          type: "trial-ended",
          palette: "error",
          icon: LockClockRounded,
          title: "Trial period ended",
          description: expiryDisplay
            ? `The trial for ${planName} ended on ${expiryDisplay}. Pick a plan to keep your workspace active.`
            : "Your trial period has finished. Choose a subscription to continue your access.",
          primaryActionLabel: "Upgrade plan",
        };
      }

      return {
        type: "trial-active",
        palette: "warning",
        icon: PlayArrowRounded,
        title: "Trial in progress",
        description:
          daysRemaining !== null
            ? `You have ${daysRemaining} day${
                daysRemaining === 1 ? "" : "s"
              } remaining in your trial. Upgrade at any time to keep your data.`
            : "Your trial is active. Upgrade whenever you're ready to keep your data.",
        primaryActionLabel: "Choose a subscription",
      };
    }

    if (
      !hasActiveSubscription ||
      expiredByDate ||
      isSubscriptionExpired ||
      statusIsEnded
    ) {
      let title = "Subscription ended";
      let description =
        "Your subscription has ended. Please choose a new plan to continue.";

      if (subscriptionEndedReason === "cancelled") {
        title = "Subscription cancelled";
        description =
          "Your subscription was cancelled. Renew to regain access.";
      } else if (subscriptionEndedReason === "expired") {
        title = "Subscription expired";
        description =
          "Your subscription has expired. Update your plan to continue using Lexmom.";
      }

      if (subscriptionMessage) {
        description = subscriptionMessage;
      }

      return {
        type: "subscription-ended",
        palette: "error",
        icon: CancelRounded,
        title,
        description: expiryDisplay
          ? `${description} The last active day was ${expiryDisplay}.`
          : description,
        primaryActionLabel: "Renew subscription",
      };
    }

    return {
      type: "subscription-active",
      palette: "success",
      icon: CheckCircleRounded,
      title: "Subscription active",
      description: expiryDisplay
        ? `Your ${planName} plan is active. The current cycle ends on ${expiryDisplay}.`
        : `Your ${planName} plan is active. Manage your subscription anytime from the billing settings.`,
      primaryActionLabel: "Change plan",
    };
  }, [
    subscriptionMessage,
    hasActiveSubscription,
    expiredByDate,
    isSubscriptionExpired,
    isTrial,
    normalizedPlan,
    expiryDisplay,
    planName,
    subscriptionEndedReason,
    subscriptionStatus,
    daysRemaining,
    loading,
    ready,
    subscriptionErrorCode,
  ]);

  const chipItems = useMemo(() => {
    const items = [];

    if (statusInfo.type !== "loading") {
      const label = toTitleCase(statusInfo.type.replace(/-/g, " "));
      items.push({ label, color: statusInfo.palette });
    }

    if (isTrial) {
      items.push({ label: "Trial", color: "warning" });
    }

    if (normalizedPlan?.billing_cycle) {
      items.push({
        label: `${toTitleCase(normalizedPlan.billing_cycle)} billing`,
        color: "default",
      });
    }

    if (subscriptionStatus) {
      const statusLabel = toTitleCase(subscriptionStatus);
      const color = ENDED_STATUSES.has(subscriptionStatus.toLowerCase())
        ? "error"
        : ACTIVE_STATUSES.has(subscriptionStatus.toLowerCase())
        ? "success"
        : "info";
      items.push({ label: `Status: ${statusLabel}`, color });
    }

    return items;
  }, [normalizedPlan, statusInfo, isTrial, subscriptionStatus]);

  const actionButtons = useMemo(() => {
    if (statusInfo.type === "loading") return [];

    const actions = [];

    const appendAction = (label, variant = "contained", disabled = false) => {
      if (!label) return;
      const normalized = label.trim();
      if (!normalized) return;
      const normalizedLower = normalized.toLowerCase();
      if (
        actions.some((action) => action.label.toLowerCase() === normalizedLower)
      ) {
        return;
      }
      const computedDisabled =
        disabled ||
        (PRICING_ACTION_LABELS.has(normalizedLower) && showPricingPage) ||
        (REFRESH_ACTION_LABELS.has(normalizedLower) &&
          isRefreshingSubscription);
      actions.push({
        label: normalized,
        variant,
        logMessage: normalized,
        disabled: computedDisabled,
      });
    };

    const primaryLabel = statusInfo.primaryActionLabel;

    if (primaryLabel) {
      const isDisabled = primaryLabel === "Browse plans" && showPricingPage;
      appendAction(primaryLabel, "contained", isDisabled);
    }

    if (statusInfo.type === "subscription-active") {
      appendAction("Change plan", "outlined");
    } else if (
      statusInfo.type.includes("trial") ||
      statusInfo.type.includes("ended") ||
      statusInfo.type.includes("inactive")
    ) {
      const isViewPlansDisabled = showPricingPage;
      appendAction("View all plans", "outlined", isViewPlansDisabled);
      appendAction("Contact support", "outlined");
    }
    return actions;
  }, [statusInfo, showPricingPage, isRefreshingSubscription]);

  const featureHighlights = useMemo(() => {
    if (!normalizedPlan || !Array.isArray(normalizedPlan.features)) return [];

    const collected = [];
    const seen = new Set();

    const pushFeature = (title, description) => {
      if (!title) return;
      const key = `${title}-${description || ""}`;
      if (seen.has(key)) return;
      seen.add(key);
      collected.push({ title, description });
    };

    normalizedPlan.features.forEach((feature) => {
      const title =
        feature.featureName || feature.mText || feature.name || feature.id;
      pushFeature(title, feature.description);

      if (Array.isArray(feature.subMenu)) {
        feature.subMenu.forEach((sub) => {
          const subTitle = sub.featureName || sub.mText || sub.name || sub.id;
          pushFeature(subTitle, sub.description);
        });
      }
    });

    return collected.slice(0, 8);
  }, [normalizedPlan]);

  // FIX: Improved planLimits to show ALL limits that exist
  const planLimits = useMemo(() => {
    if (!normalizedPlan) return [];

    return limitDefinitions
      .map(({ key, label }) => {
        // Check if the key exists in the plan
        if (!Object.prototype.hasOwnProperty.call(normalizedPlan, key)) {
          return null;
        }

        const rawValue = normalizedPlan[key];

        // Skip if value is explicitly undefined
        if (rawValue === undefined) return null;

        const numericValue =
          typeof rawValue === "number" ? rawValue : Number(rawValue);

        const isUnlimited =
          rawValue === null ||
          (Number.isFinite(numericValue) && numericValue <= 0);

        const formattedValue = isUnlimited
          ? "Unlimited"
          : formatCount(
              Number.isFinite(numericValue) ? numericValue : rawValue
            );

        return { label, value: formattedValue };
      })
      .filter(Boolean); // Show all available limits, not just first 6
  }, [normalizedPlan, formatCount]);

  const StatusIcon = statusInfo.icon;

  const handleOpenPricingPage = useCallback((action) => {
    // const label = action?.logMessage || action?.label || "Plan action";
    // toast.info(`${label} - Opening pricing options.`);
    setShowPricingPage(true);
  }, []);

  const handleRefreshSubscription = useCallback(
    async (action) => {
      if (isRefreshingSubscription) return;
      if (!token) {
        toast.error("You need to be signed in to manage your subscription.");
        return;
      }

      const label = action?.logMessage || action?.label || "Subscription";
      setIsRefreshingSubscription(true);
      toast.info(`${label} - Refreshing your subscription details...`);

      try {
        await dispatch(
          getMySubscription({ token, teamMemberLawyerId })
        ).unwrap();
        try {
          await dispatch(getMyUsage({ token, teamMemberLawyerId })).unwrap();
        } catch (usageFetchError) {
          const message = resolveErrorMessage(
            usageFetchError,
            "Subscription updated but usage details couldn't be refreshed."
          );
          toast.warn(message);
        }
        toast.success("Subscription details refreshed.");
      } catch (error) {
        const message = resolveErrorMessage(
          error,
          "We couldn't refresh your subscription. Please try again later."
        );
        toast.error(message);
      } finally {
        setIsRefreshingSubscription(false);
      }
    },
    [dispatch, token, teamMemberLawyerId, isRefreshingSubscription]
  );

  const handleContactSupport = useCallback((action) => {
    const label = action?.logMessage || action?.label || "Support";
    const subject = encodeURIComponent("Lexmom subscription assistance");
    const body = encodeURIComponent(
      "Hello Lexmom support team,%0D%0A%0D%0AI need help with my subscription.%0D%0A"
    );
    const mailtoLink = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;

    if (typeof window !== "undefined") {
      window.open(mailtoLink, "_blank");
    }
    toast.info(`${label} - Opening your email client.`);
  }, []);

  const handleFallbackAction = useCallback((action) => {
    const label = action?.logMessage || action?.label || "Action";
    toast.info(`${label} clicked - This feature is in development mode.`);
  }, []);

  if (loading || !ready) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="25rem"
      >
        <Typography variant="h6" color="text.secondary">
          Loading subscription information...
        </Typography>
      </Box>
    );
  }

  if (showPricingPage) { 
    return <PricingPage inSubscriptionPage={true} />;
  }

  return (
    <Box>
      <AnimatePresence mode="wait">
        <motion.div
          key={statusInfo.type}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.4 }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md={lockScreen ? 12 : 6}>
              <Card elevation={3} sx={{ borderRadius: "0.75rem" }}>
                <CardContent>
                  <Stack spacing={3}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <StatusIcon
                        sx={{
                          fontSize: "2rem",
                          color: (theme) =>
                            theme.palette[statusInfo.palette].main,
                        }}
                      />
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {statusInfo.title}
                        </Typography>
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          sx={{ mt: "0.25rem" }}
                        >
                          {statusInfo.description}
                        </Typography>
                      </Box>
                    </Stack>

                    {chipItems.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          gap: "0.5rem",
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        {chipItems?.map((chip) => (
                          <Chip
                            key={chip.label}
                            label={chip.label}
                            size="small"
                            color={
                              chip.color !== "default" ? chip.color : undefined
                            }
                            variant={
                              chip.color === "default" ? "outlined" : "filled"
                            }
                          />
                        ))}
                        {expiryDisplay && (
                          <Chip
                            icon={
                              <CalendarMonthRounded
                                sx={{ fontSize: "0.7rem" }}
                              />
                            }
                            label={`Ends on ${expiryDisplay}`}
                            variant="outlined"
                            size="small"
                          />
                        )}
                        {nextBillingDisplay && !isTrial && (
                          <Chip
                            icon={
                              <FlashOnRounded sx={{ fontSize: "0.7rem" }} />
                            }
                            label={`Next billing ${nextBillingDisplay}`}
                            variant="outlined"
                            size="small"
                          />
                        )}
                      </Box>
                    )}

                    {isSubscriptionEnding &&
                      statusInfo.type === "subscription-active" && (
                        <Alert severity="warning">
                          <AlertTitle sx={{ fontWeight: 700 }}>
                            Renewal reminder
                          </AlertTitle>
                          Your subscription renews soon. Review your plan if you
                          wish to make changes before the next billing cycle.
                        </Alert>
                      )}

                    {subscriptionError && (
                      <Alert severity="error">
                        <AlertTitle sx={{ fontWeight: 700 }}>
                          Subscription Error
                        </AlertTitle>
                        {subscriptionMessage ||
                          "There was an issue with your subscription."}
                      </Alert>
                    )}

                    {actionButtons.length > 0 && (
                      <SubscriptionActionButtons
                        actions={actionButtons}
                        onOpenPricing={handleOpenPricingPage}
                        onRefreshSubscription={handleRefreshSubscription}
                        onContactSupport={handleContactSupport}
                        onFallback={handleFallbackAction}
                      />
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {!lockScreen && (
              <>
                <Grid item xs={12} md={6}>
                  <Card
                    elevation={2}
                    sx={{ borderRadius: "0.75rem", height: "100%" }}
                  >
                    <CardContent>
                      <Stack spacing={2}>
                        <Typography variant="h6" sx={{ fontWeight: "600" }}>
                          Plan overview
                        </Typography>
                        <Divider />
                        <Stack spacing={1.5}>
                          <DetailRow label="Plan" value={planName} />
                          <DetailRow
                            label="Billing cycle"
                            value={
                              normalizedPlan?.billing_cycle
                                ? toTitleCase(normalizedPlan.billing_cycle)
                                : "Not specified"
                            }
                          />
                          <DetailRow
                            label="Plan price"
                            value={
                              normalizedPlan?.price
                                ? `${normalizedPlan.price}Rs`
                                : normalizedPlan?.price_dollar
                                ? `${normalizedPlan.price_dollar}usd`
                                : "Based on usage"
                            }
                          />
                          <DetailRow
                            label="Current status"
                            value={toTitleCase(statusInfo.title)}
                          />
                          <DetailRow
                            label="Cycle ends / Next Billing"
                            value={expiryDisplay || "Not scheduled"}
                          />
                          {/* <DetailRow
                            label="Next billing"
                            value={nextBillingDisplay || "Not scheduled"}
                          /> */}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card
                    elevation={2}
                    sx={{ borderRadius: "0.75rem", height: "100%" }}
                  >
                    <CardContent>
                      <Stack spacing={2} sx={{ height: "100%" }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, letterSpacing: "0.0125rem" }}
                        >
                          Feature highlights
                        </Typography>
                        <Divider />
                        {featureHighlights.length > 0 ? (
                          <List dense sx={{ py: 0 }}>
                            {featureHighlights.map((feature) => (
                              <ListItem
                                key={feature.title}
                                alignItems="flex-start"
                                sx={{ py: "0.313rem" }}
                              >
                                <ListItemIcon sx={{ minWidth: "1.875rem" }}>
                                  <CheckCircleRounded
                                    color="success"
                                    fontSize="small"
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    <Typography
                                      variant="subtitle2"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {feature.title}
                                    </Typography>
                                  }
                                  secondary={
                                    feature.description ? (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {feature.description}
                                      </Typography>
                                    ) : null
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Plan features will appear here once you select a
                            subscription.
                          </Typography>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card elevation={2} sx={{ borderRadius: "0.75rem" }}>
                    <CardContent>
                      <Stack spacing={3}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: "600" }}>
                            Usage limits
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: "0.313rem" }}
                          >
                            Monitor how much of each quota your team has used
                            this cycle.
                          </Typography>
                        </Box>
                        {usageErrorMessage && (
                          <Alert severity="error" variant="outlined">
                            <AlertTitle sx={{ fontWeight: 700 }}>
                              Usage data unavailable
                            </AlertTitle>
                            {usageErrorMessage}
                          </Alert>
                        )}
                        {usageLoading ? (
                          <Stack spacing={1.5}>
                            {Array.from({ length: 4 }).map((_, index) => (
                              <Box key={`usage-skeleton-${index}`}>
                                <Skeleton variant="text" width="40%" />
                                <Skeleton
                                  variant="rounded"
                                  height="0.625rem"
                                  sx={{
                                    borderRadius: "0.313rem",
                                    mt: "0.469rem",
                                  }}
                                />
                              </Box>
                            ))}
                          </Stack>
                        ) : usageMetrics.length > 0 ? (
                          <Stack spacing={2}>
                            {chartHasData ? (
                              <Box sx={{ height: "16.25rem", width: "100%" }}>
                                <ResponsiveContainer width="100%" height="100%">
                                  <LineChart
                                    data={usageChartData}
                                    margin={{
                                      top: 16,
                                      right: 16,
                                      left: 8,
                                      bottom: 8,
                                    }}
                                  >
                                    <CartesianGrid
                                      strokeDasharray="3 3"
                                      vertical={false}
                                    />
                                    <XAxis
                                      dataKey="name"
                                      tick={{
                                        fontSize: "0.688rem",
                                        display: "none",
                                      }}
                                      tickFormatter={axisNameFormatter}
                                      interval={0}
                                      height={50}
                                    />
                                    <YAxis
                                      tick={{ fontSize: "0.688rem" }}
                                      tickFormatter={axisTickFormatter}
                                    />
                                    <RechartsTooltip
                                      formatter={(value, name) => [
                                        formatCount(value),
                                        name,
                                      ]}
                                      labelStyle={{
                                        fontSize: "0.75rem",
                                        fontWeight: 600,
                                      }}
                                      contentStyle={{
                                        backgroundColor: "#fff",
                                        borderRadius: "0.75rem",
                                        border: `0.063rem solid ${theme.palette.divider}`,
                                        boxShadow:
                                          "0rem 0.375rem 1.125rem rgba(0,0,0,0.1)",
                                      }}
                                    />
                                    <Legend
                                      wrapperStyle={{ fontSize: "0.75rem" }}
                                    />
                                    <Line
                                      type="monotone"
                                      dataKey="total"
                                      name="Total limit"
                                      stroke={totalColor}
                                      strokeWidth={2}
                                      dot={{
                                        r: 4,
                                        strokeWidth: 2,
                                        stroke: totalColor,
                                      }}
                                      activeDot={{ r: 6 }}
                                    />
                                    <Line
                                      type="monotone"
                                      dataKey="used"
                                      name="Usage"
                                      stroke={usageColor}
                                      strokeWidth={3}
                                      dot={{
                                        r: 4,
                                        strokeWidth: 2,
                                        stroke: usageColor,
                                      }}
                                      activeDot={{ r: 6 }}
                                    />
                                  </LineChart>
                                </ResponsiveContainer>
                              </Box>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Usage metrics will appear once your plan has
                                trackable limits.
                              </Typography>
                            )}

                            {chartHasData && !chartHasUsageValues && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                We'll display usage as soon as data becomes
                                available for these limits.
                              </Typography>
                            )}

                            {unlimitedUsageMetrics.length > 0 && (
                              <Stack spacing={0.5}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  Unlimited on your plan
                                </Typography>
                                {unlimitedUsageMetrics.map((metric) => (
                                  <Typography
                                    key={`unlimited-${metric.key}`}
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    • {metric.label}
                                  </Typography>
                                ))}
                              </Stack>
                            )}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Usage information will appear once it is available
                            for your account.
                          </Typography>
                        )}
                        <Divider />
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600 }}
                          >
                            Limits
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: "0.313rem" }}
                          >
                            A quick overview of your plan’s usage and remaining
                            limits
                          </Typography>
                        </Box>
                        {planLimits.length > 0 ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              overflow: "auto",
                              gap: "0.75rem",
                              ...pageScrollStyle,
                            }}
                          >
                            {planLimits.map((limit) => (
                              <Chip
                                key={limit.label}
                                label={
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "0.5rem",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        textTransform: "uppercase",
                                        fontWeight: 600,
                                        fontSize: "0.7rem",
                                        letterSpacing: "0.05rem",
                                        color: "text.secondary",
                                      }}
                                    >
                                      {limit.label}:
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: "0.7rem",
                                        color: "text.primary",
                                      }}
                                    >
                                      {limit.value}
                                    </Typography>
                                  </Box>
                                }
                                variant="outlined"
                                sx={{
                                  borderRadius: "0.75rem",
                                  px: 1,
                                  py: 0.5,
                                  borderColor: "#e0e0e0",
                                  bgcolor: "#fafafa",
                                }}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Limit information will appear here once a plan is
                            associated with your account.
                          </Typography>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </>
            )}
          </Grid>
        </motion.div>
      </AnimatePresence>
    </Box>
  );
};

const DetailRow = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "1rem",
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "right" }}>
      {value ?? "—"}
    </Typography>
  </Box>
);

export default SubscriptionStatusPage;
